import type { Resource, ResourceKey } from '@eaphone/types/resources';

import { Ky, KyJson } from './index.ts';
import type { List } from './lib.d.ts';
import type { CustomOptions } from './type.d.ts';

export const Network = Object.freeze({
  resource: <key extends ResourceKey, Struct extends Resource[key]>(
    resource: key,
  ) =>
    Object.freeze({
      select: Object.freeze({
        id: (id: string) =>
          KyJson.get<Struct & { id: string }>(`${resource}/${id}`),
        batchId: (batchId: string) =>
          KyJson.get<Struct & { id: string }>(
            `${resource}/batchId/${batchId}/`,
          ),
        serial: (serial: string) =>
          KyJson.get<Struct>(`${resource}/serial/${serial}/`),
      }),
    }),
  custom: Object.freeze({
    get: <key extends ResourceKey, Struct extends Resource[key]>(
      resource: key,
      options?: CustomOptions,
    ) => KyJson.get<Struct & { id: string }>(resource, options),
    file: <key extends ResourceKey, Struct extends Resource[key]>(
      resource: key,
      options?: CustomOptions & { file: File },
    ) => KyJson.file<Struct>(resource, options),
    post: <key extends ResourceKey, Struct extends Resource[key]>(
      resource: key,
      options?: CustomOptions & {
        json?: Partial<Struct>;
      },
    ) => KyJson.post<Struct & { id: string }>(resource, options),
    action: <key extends ResourceKey, Struct extends Resource[key]>(
      resource: key,
      options?: CustomOptions & { json?: Record<string, any> },
    ) =>
      options?.json
        ? KyJson.post<Struct>(resource, options)
        : KyJson.get<Struct>(resource, options),
    patch: <key extends ResourceKey, Struct extends Resource[key]>(
      resource: key,
      options?: CustomOptions &
        ({ json: Partial<Struct> } | { searchParams?: Record<string, any> }),
    ) => KyJson.patch<Struct & { id: string }>(resource, options),
    delete: <key extends ResourceKey>(resource: key, options?: CustomOptions) =>
      KyJson.delete<never>(resource, options),
    list: <key extends ResourceKey, Struct extends Resource[key]>(
      resource: key,
      options?: CustomOptions & {
        searchParams:
          | {
              sort?: 'DESC' | 'ASC';
              sortField?: string;
              page_index: number;
              page_size: number;
            }
          | {
              sort: 'DESC' | 'ASC';
              sortField?: string;
            };
      },
    ) =>
      KyJson.get<List<Struct & { id: string }>>(`${resource}/`, options).then(
        ({ list, ...rest }) => ({ ...rest, list }),
      ),
  }),
  raw: Object.freeze({
    get: <key extends ResourceKey>(resource: key, options?: CustomOptions) =>
      Ky.get(resource, options),
    post: <key extends ResourceKey>(resource: key, options?: CustomOptions) =>
      Ky.post(resource, options),
    patch: <key extends ResourceKey>(resource: key, options: CustomOptions) =>
      Ky.patch(resource, options),
  }),
});
